import { useEffect, useState } from "react";
import moment from "moment-timezone";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  OtcContractLayerDto,
  OtcLayerInvoiceFeeInput,
  OtcLayerInvoiceFeeInputRefined,
  BulkCreateOtcLayerInvoiceFeeItem,
  useCreateOtcLayerInvoiceFee,
  useBulkCreateOtcLayerInvoiceFees,
} from "@/lib/api/otc";
import {
  OtcLayerInvoiceFeeForm,
  OtcLayerInvoiceFeeFormErrors,
  emptyInvoiceFeeValues,
  layerToInvoiceFeeValues,
  formInvoiceFeeValuesToApi,
} from "./OtcLayerInvoiceFeeForm";
import { LayerEconomicsSummary } from "./LayerEconomicsSummary";
import { generateAmendmentTitleForLayer } from "./common";
import { formatDateWithoutTimezoneToFormat } from "@/lib/api/date";

/** A layer already has an invoice fee when its invoice_number is populated. */
export function layerHasInvoiceFee(layer: OtcContractLayerDto): boolean {
  return layer.invoice_fee_info.invoice_number.trim() !== "";
}

/** Maps zod issues onto the per-field error map used by the form. */
function issuesToErrors(
  issues: { path: (string | number)[]; message: string }[],
): OtcLayerInvoiceFeeFormErrors {
  const errors: OtcLayerInvoiceFeeFormErrors = {};
  for (const issue of issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !(key in errors)) {
      errors[key as keyof OtcLayerInvoiceFeeFormErrors] = issue.message;
    }
  }
  return errors;
}

interface CreateInvoiceFeeModalProps {
  open: boolean;
  contract_id: string;
  /**
   * "single" renders a form for exactly one layer and POSTs to the single
   * endpoint. "bulk" renders a form per layer, disabling layers that already
   * have a fee, and POSTs the enabled layers as one array to the bulk endpoint.
   */
  mode: "single" | "bulk";
  layers: OtcContractLayerDto[];
  onClose: () => void;
  onCreated: () => void;
}

export function CreateInvoiceFeeModal({
  open,
  contract_id,
  mode,
  layers,
  onClose,
  onCreated,
}: CreateInvoiceFeeModalProps) {
  const createOne = useCreateOtcLayerInvoiceFee(contract_id);
  const createBulk = useBulkCreateOtcLayerInvoiceFees(contract_id);

  const [valuesByLayer, setValuesByLayer] = useState<
    Record<string, OtcLayerInvoiceFeeInput>
  >({});
  const [errorsByLayer, setErrorsByLayer] = useState<
    Record<string, OtcLayerInvoiceFeeFormErrors>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Seed fresh values for every layer each time the modal opens. Editable
  // layers (no existing fee) default invoice_date to today; disabled layers
  // (already have a fee) are seeded from their existing fee so the disabled
  // form displays the current values instead of an empty form.
  useEffect(() => {
    if (!open) return;
    const today = formatDateWithoutTimezoneToFormat(new Date(), "YYYY-MM-DD");
    const seeded: Record<string, OtcLayerInvoiceFeeInput> = {};
    for (const layer of layers) {
      const disabled = mode === "bulk" && layerHasInvoiceFee(layer);
      if (disabled) {
        seeded[layer.id] = layerToInvoiceFeeValues(layer);
        continue;
      }
      const values = emptyInvoiceFeeValues();
      values.invoice_date = today;
      values.date_marked = moment(today).format("YYYY-MM-DD");
      seeded[layer.id] = values;
    }
    setValuesByLayer(seeded);
    setErrorsByLayer({});
    setSubmitError(null);
  }, [open, layers, mode]);

  const isLayerDisabled = (layer: OtcContractLayerDto): boolean =>
    mode === "bulk" && layerHasInvoiceFee(layer);

  const editableLayers = layers.filter(
    (layer: OtcContractLayerDto) => !isLayerDisabled(layer),
  );

  const setLayerValues = (
    layer_id: string,
    values: OtcLayerInvoiceFeeInput,
  ) => {
    setValuesByLayer((prev) => ({ ...prev, [layer_id]: values }));
  };

  const validateAll = (): boolean => {
    const nextErrors: Record<string, OtcLayerInvoiceFeeFormErrors> = {};
    let ok = true;
    for (const layer of editableLayers) {
      const values = valuesByLayer[layer.id] ?? emptyInvoiceFeeValues();
      const parsed = OtcLayerInvoiceFeeInputRefined.safeParse(values);
      if (!parsed.success) {
        ok = false;
        nextErrors[layer.id] = issuesToErrors(parsed.error.issues);
      }
    }
    setErrorsByLayer(nextErrors);
    return ok;
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    setSubmitError(null);

    if (editableLayers.length === 0) {
      setSubmitError("There are no layers available to create an invoice fee for.");
      return;
    }
    if (!validateAll()) return;

    setIsSaving(true);
    try {
      if (mode === "single") {
        const layer = editableLayers[0];
        const result = await createOne(layer.id, formInvoiceFeeValuesToApi(valuesByLayer[layer.id]));
        if (!result.success) {
          setSubmitError(result.message || "Failed to create invoice fee.");
          return;
        }
      } else {
        const items: BulkCreateOtcLayerInvoiceFeeItem[] = editableLayers.map(
          (layer: OtcContractLayerDto) => ({
            ...formInvoiceFeeValuesToApi(valuesByLayer[layer.id]),
            trading_contract_layer_id: layer.id,
          }),
        );
        const result = await createBulk(items);
        if (!result.success) {
          setSubmitError(result.message || "Failed to create invoice fees.");
          return;
        }
      }
      onCreated();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    if (!next) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-none w-[70vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {mode === "bulk" ? "Create Invoice Fees" : "Create Invoice Fee"}
          </DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to create</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {layers.map((layer: OtcContractLayerDto) => {
            const disabled = isLayerDisabled(layer);
            const values = valuesByLayer[layer.id] ?? emptyInvoiceFeeValues();
            return (
              <div
                key={layer.id}
                className="rounded-lg border p-4 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">
                    Layer #{layer.layer_no} —{" "}
                    {generateAmendmentTitleForLayer(layer)}
                  </span>
                  {disabled && (
                    <Badge variant="secondary">Already has invoice fee</Badge>
                  )}
                </div>

                <LayerEconomicsSummary layer={layer} />

                <OtcLayerInvoiceFeeForm
                  values={values}
                  onChange={(next) => setLayerValues(layer.id, next)}
                  errors={errorsByLayer[layer.id]}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || editableLayers.length === 0}
          >
            {isSaving ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
